/**
 * InvitationsTab - Pending and accepted invitations
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { Copy, Check } from 'lucide-react';
import { Badge } from '../../components/ui/Badge';
import invitationService, { Invitation } from '../../services/api/invitationService';
import { useAuthStore } from '../../stores/authStore';

// ============================================================================
// Styled Components
// ============================================================================

const Section = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[8]};
`;

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
`;

const TableWrapper = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  text-align: left;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: ${({ theme }) => theme.colors.backgrounds.page};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
`;

const Td = styled.td`
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  vertical-align: middle;
`;

const Tr = styled.tr`
  &:last-child td {
    border-bottom: none;
  }

  &:hover td {
    background: ${({ theme }) => theme.colors.backgrounds.light};
  }
`;

const EmailCell = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const EmailText = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const NameText = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  justify-content: flex-end;
`;

const ResendBtn = styled.button`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.primary.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    text-decoration: underline;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RevokeBtn = styled.button`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.error.text};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    text-decoration: underline;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CopyLinkBtn = styled.button<{ $copied?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme, $copied }) =>
    $copied ? theme.colors.status.success.text : theme.colors.text.secondary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  transition: ${({ theme }) => theme.transitions.fast};

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.primary.base};
    text-decoration: underline;
  }
`;

const EmptyState = styled.div`
  padding: ${({ theme }) => theme.spacing[6]};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const LoadingState = styled(EmptyState)``;

const NoneText = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ThRight = styled(Th)`
  text-align: right;
`;

// ============================================================================
// Helpers
// ============================================================================

const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

// ============================================================================
// Row Components
// ============================================================================

const PendingRow = ({
  inv,
  onResend,
  onRevoke,
  onCopyLink,
  isResending,
  isRevoking,
  copiedId,
  canResend,
  canRevoke,
}: {
  inv: Invitation;
  onResend: (id: string) => void;
  onRevoke: (id: string) => void;
  onCopyLink: (id: string, link: string) => void;
  isResending: boolean;
  isRevoking: boolean;
  copiedId: string | null;
  canResend: boolean;
  canRevoke: boolean;
}) => (
  <Tr>
    <Td>
      <EmailCell>
        <EmailText>{inv.email}</EmailText>
        {inv.name && <NameText>{inv.name}</NameText>}
      </EmailCell>
    </Td>
    <Td>
      {inv.roles && inv.roles.length > 0 ? (
        <Badge roleName={inv.roles[0].name}>{inv.roles[0].name}</Badge>
      ) : (
        <NoneText>—</NoneText>
      )}
    </Td>
    <Td>{formatDate(inv.createdAt)}</Td>
    <Td>{formatDate(inv.expiresAt)}</Td>
    <Td>
      <ActionsCell>
        {inv.inviteLink && (
          <CopyLinkBtn
            $copied={copiedId === inv.id}
            onClick={() => onCopyLink(inv.id, inv.inviteLink!)}
          >
            {copiedId === inv.id ? <><Check /> Copied</> : <><Copy /> Copy Link</>}
          </CopyLinkBtn>
        )}
        {canResend && (
          <ResendBtn onClick={() => onResend(inv.id)} disabled={isResending}>
            Resend
          </ResendBtn>
        )}
        {canRevoke && (
          <RevokeBtn onClick={() => onRevoke(inv.id)} disabled={isRevoking}>
            Revoke
          </RevokeBtn>
        )}
      </ActionsCell>
    </Td>
  </Tr>
);

const AcceptedRow = ({ inv }: { inv: Invitation }) => (
  <Tr>
    <Td>
      <EmailCell>
        <EmailText>{inv.email}</EmailText>
        {inv.name && <NameText>{inv.name}</NameText>}
      </EmailCell>
    </Td>
    <Td>
      {inv.roles && inv.roles.length > 0 ? (
        <Badge roleName={inv.roles[0].name}>{inv.roles[0].name}</Badge>
      ) : (
        <NoneText>—</NoneText>
      )}
    </Td>
    <Td>{formatDate(inv.createdAt)}</Td>
    <Td>{inv.acceptedAt ? formatDate(inv.acceptedAt) : '—'}</Td>
  </Tr>
);

// ============================================================================
// Main Component
// ============================================================================

export const InvitationsTab = () => {
  const queryClient = useQueryClient();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const { hasPermission } = useAuthStore();

  const { data, isLoading } = useQuery({
    queryKey: ['invitations'],
    queryFn: () => invitationService.getInvitations(),
  });

  const resendMutation = useMutation({
    mutationFn: (id: string) => invitationService.resendInvitation(id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      // Auto-copy the refreshed invite link
      if (result?.inviteLink) {
        navigator.clipboard.writeText(result.inviteLink).catch(() => {});
        setCopiedId(result.id);
        setTimeout(() => setCopiedId(null), 2000);
      }
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => invitationService.revokeInvitation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
    },
  });

  const handleCopyLink = async (id: string, link: string) => {
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = link;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const pending = data?.pending ?? [];
  const accepted = data?.accepted ?? [];

  if (isLoading) {
    return <LoadingState>Loading invitations...</LoadingState>;
  }

  return (
    <>
      {/* Pending Section */}
      <Section>
        <SectionTitle>Pending ({pending.length})</SectionTitle>
        <TableWrapper>
          {pending.length === 0 ? (
            <EmptyState>No pending invitations</EmptyState>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Email</Th>
                  <Th>Role</Th>
                  <Th>Invited</Th>
                  <Th>Expires</Th>
                  <ThRight>Actions</ThRight>
                </tr>
              </thead>
              <tbody>
                {pending.map((inv) => (
                  <PendingRow
                    key={inv.id}
                    inv={inv}
                    onResend={(id) => resendMutation.mutate(id)}
                    onRevoke={(id) => revokeMutation.mutate(id)}
                    onCopyLink={handleCopyLink}
                    isResending={resendMutation.isPending && resendMutation.variables === inv.id}
                    isRevoking={revokeMutation.isPending && revokeMutation.variables === inv.id}
                    copiedId={copiedId}
                    canResend={hasPermission('team:manage')}
                    canRevoke={hasPermission('team:manage')}
                  />
                ))}
              </tbody>
            </Table>
          )}
        </TableWrapper>
      </Section>

      {/* Accepted Section */}
      <Section>
        <SectionTitle>Accepted ({accepted.length})</SectionTitle>
        <TableWrapper>
          {accepted.length === 0 ? (
            <EmptyState>No accepted invitations</EmptyState>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Email</Th>
                  <Th>Role</Th>
                  <Th>Invited</Th>
                  <Th>Joined</Th>
                </tr>
              </thead>
              <tbody>
                {accepted.map((inv) => (
                  <AcceptedRow key={inv.id} inv={inv} />
                ))}
              </tbody>
            </Table>
          )}
        </TableWrapper>
      </Section>

    </>
  );
};

export default InvitationsTab;
